/**
 * The ordered lists of section 4, in plain words: a series' parts (seasons of episodes, modules of
 * lessons, a programme's weeks of days, numbered for the apps as listed), a collection's items, and
 * the categories in a list. Items are picker cards; parts and categories get their ids here.
 */

import { useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import type { EntityType } from "@/api/admin";
import { slugify, uniqueId } from "@/lib/ids";
import { moveItem } from "@/screens/rows";
import { useStudio } from "@/shell/studio";
import { lower, noun } from "@/vocabulary/vocabulary";
import { useForm } from "../form-context";
import { fieldDomId } from "../paths";
import type { SectionPlan } from "../plan";
import { OptionSelect } from "../controls/field-control";
import { LocalizedField } from "../controls/inputs";
import { ItemPicker, PickerCard, RowActions } from "../controls/picker";

interface Part { id: string; number: number; title?: Record<string, string>; titleIds: string[] }

/** Refs of one type as an ordered list of cards, with Add. */
function CardList({ ids, types, onChange, numbering, addLabel, pickerTitle }: { ids: string[]; types: EntityType[]; onChange: (ids: string[]) => void; numbering?: (i: number) => string; addLabel: string; pickerTitle: string }) {
  const form = useForm();
  const [open, setOpen] = useState(false);
  const find = (id: string) => form.catalogue?.find((e) => types.includes(e.type) && e.id === id);
  return (
    <div className="flex flex-col gap-2">
      {ids.length ? (
        <ol className="flex flex-col gap-2">
          {ids.map((id, i) => (
            <li key={`${id}:${i}`}>
              <PickerCard entity={find(id)} missingId={id} index={numbering?.(i)} actions={form.canEdit ? <RowActions name={find(id)?.label ?? id} index={i} count={ids.length} onMove={(to) => onChange(moveItem(ids, i, to))} onRemove={() => onChange(ids.filter((_, j) => j !== i))} /> : undefined} />
            </li>
          ))}
        </ol>
      ) : null}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setOpen(true)}>{addLabel}</Button></div> : null}
      <ItemPicker open={open} onClose={() => setOpen(false)} types={types} catalogue={form.catalogue} exclude={ids} title={pickerTitle} onPick={(picked) => onChange([...ids, ...picked])} />
    </div>
  );
}

export function PartsSection({ section }: { section: Extract<SectionPlan, { id: "parts" }> }) {
  const form = useForm();
  const parts = (form.doc["seasons"] as Part[] | undefined) ?? [];
  const write = (next: Part[]) => form.set("seasons", next.map((p, i) => ({ ...p, number: i + 1 })), true);
  const offset = (i: number) => parts.slice(0, i).reduce((n, p) => n + p.titleIds.length, 0);
  const taken = parts.map((p) => p.id);
  const addPart = () => {
    let n = parts.length + 1;
    while (taken.includes(`${form.id}-${n}`)) n++;
    write([...parts, { id: `${form.id}-${n}`, number: parts.length + 1, title: { [form.locale]: `${section.partOne} ${parts.length + 1}` }, titleIds: [] }]);
  };
  return (
    <div className="flex flex-col gap-4">
      {parts.map((p, i) => (
        <section key={p.id} id={fieldDomId(`seasons.${i}`)} tabIndex={-1} className="flex flex-col gap-3 rounded-2xl border border-s-subtle p-4 outline-none" aria-label={`${section.partOne} ${i + 1}`}>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 grow"><LocalizedField path={`seasons.${i}.title`} label={`${section.partOne} ${i + 1}: name`} size="sm" /></div>
            {form.canEdit ? <div className="flex gap-1 pb-1"><RowActions name={`${section.partOne.toLowerCase()} ${i + 1}`} index={i} count={parts.length} onMove={(to) => write(moveItem(parts, i, to))} onRemove={() => write(parts.filter((_, j) => j !== i))} /></div> : null}
          </div>
          <CardList ids={p.titleIds} types={["title"]} onChange={(ids) => write(parts.map((x, j) => (j === i ? { ...x, titleIds: ids } : x)))}
            numbering={(k) => (section.numbering === "continuous" ? `${section.itemOne} ${offset(i) + k + 1}` : `${k + 1}`)}
            addLabel={`Add ${lower(section.itemMany)}`} pickerTitle={`Add ${lower(section.itemMany)} to ${section.partOne.toLowerCase()} ${i + 1}`} />
        </section>
      ))}
      {parts.length === 0 ? <p className="text-body-2 text-t-secondary">{`No ${lower(section.partMany)} yet.`}</p> : null}
      {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={addPart}>{`Add a ${lower(section.partOne)}`}</Button></div> : null}
    </div>
  );
}

export function ItemsSection() {
  const form = useForm();
  const { pack } = useStudio();
  const ids = (form.doc["itemIds"] as string[] | undefined) ?? [];
  return <CardList ids={ids} types={["title", "series", "liveEvent"]} numbering={(i) => `${i + 1}`} onChange={(next) => form.set("itemIds", next, true)}
    addLabel={`Add ${lower(noun(pack, "title", "many"))} and more`} pickerTitle={`Add to “${form.doc["title"] ? (form.doc["title"] as Record<string, string>)[form.locale] ?? "" : ""}”`} />;
}

interface Term { id: string; title: Record<string, string>; parentId?: string }

export function TermsSection() {
  const form = useForm();
  const terms = (form.doc["terms"] as Term[] | undefined) ?? [];
  const [name, setName] = useState("");
  const write = (next: Term[]) => form.set("terms", next, true);
  const add = () => {
    if (!name.trim()) return;
    const id = uniqueId(name, new Set(terms.map((t) => t.id)), `${slugify(form.id)}/`);
    write([...terms, { id, title: { [form.locale]: name.trim() } }]);
    setName("");
  };
  return (
    <div className="flex flex-col gap-3">
      {terms.length ? (
        <ol className="flex flex-col gap-2">
          {terms.map((t, i) => (
            <li key={t.id} className="grid items-start gap-2 rounded-2xl bg-b-surface1 p-3 md:grid-cols-[minmax(0,1fr)_14rem_auto]">
              <LocalizedField path={`terms.${i}.title`} label="Name" size="sm" required />
              <OptionSelect label="Inside" value={t.parentId} allowNone disabled={!form.canEdit} options={terms.filter((x) => x.id !== t.id).map((x) => ({ value: x.id, label: x.title[form.locale] ?? Object.values(x.title)[0] ?? x.id }))}
                onChange={(v) => form.set(`terms.${i}.parentId`, v)} />
              {form.canEdit ? <div className="flex gap-1 md:mt-7"><RowActions name={t.title[form.locale] ?? t.id} index={i} count={terms.length} onMove={(to) => write(moveItem(terms, i, to))} onRemove={() => write(terms.filter((_, j) => j !== i))} /></div> : null}
            </li>
          ))}
        </ol>
      ) : <p className="text-body-2 text-t-secondary">Nothing in this list yet.</p>}
      {form.canEdit ? (
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <Field className="grow" label="Add to the list" size="sm" placeholder="e.g. Documentary" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" size="sm" variant="secondary" icon="plus" disabled={!name.trim()}>Add</Button>
        </form>
      ) : null}
    </div>
  );
}

